import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import _snakeCase from 'lodash/snakeCase';
import { showMessage } from 'app/store/fuse/messageSlice';
import { endReadOnlySession, logoutUser } from 'src/app/auth/store/userSlice';
import store from 'src/app/store';
import { AxiosManager } from 'src/app/services/axiosManager';
import { ExceptionCode } from 'src/app/constants/exceptionCode';

import jwtService from 'app/services/jwtService';
import { IMPERSONATION_TRANSLATION_KEY } from 'app/impersonation/i18n';
import { createAuthErrorHandler } from 'app/impersonation/authErrorHandler';

import en from './i18n/en';
import ua from './i18n/ua';

i18next.addResourceBundle('en-US', 'common-errors', en);
i18next.addResourceBundle('uk-UA', 'common-errors', ua);

// The backend computes the OTP resend cooldown from configuration and
// reports the real seconds left as `secondsLeft` on the error response
// (see `ResponseErrorEntity`). Use that field directly instead of parsing
// the message prose; if it's absent (an older backend, or any other
// reason), fall back to a message that names no number rather than
// guessing one.
const buildErrorMessage = (t, key, err) => {
  const translationKey = _snakeCase(key).toUpperCase();

  if (key === 'OneTimePasswordAlreadySent') {
    const seconds = err?.response?.data?.secondsLeft;
    if (typeof seconds === 'number') {
      return t(translationKey, { seconds });
    }
    return t(`${translationKey}_FALLBACK`);
  }

  return t(translationKey);
};

// A read-only admin session cannot be refreshed: on an expired token it ends
// instead of trying the refresh token.
const handleAuthError = createAuthErrorHandler({
  jwtService,
  onImpersonationEnded: () => {
    store.dispatch(
      showMessage({
        message: i18next.t(`${IMPERSONATION_TRANSLATION_KEY}:SESSION_EXPIRED`),
        autoHideDuration: 10000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        variant: 'warning',
      })
    );
    store.dispatch(endReadOnlySession());
  },
});

export default function Notification() {
  const { t } = useTranslation('common-errors');
  const handleShowErrorMessage = (text) =>
    store.dispatch(
      showMessage({
        message: text,
        autoHideDuration: 5000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        variant: 'error',
      })
    );

  useEffect(() => {
    const excludedKeys = ['AuthError', 'Unauthorized', 'Forbidden'];
    AxiosManager.bindHttpErrorsHandlers(
      new Map([
        // REPLACE WITH 403
        [ExceptionCode.AuthError, handleAuthError],
        [ExceptionCode.Unauthorized, () => store.dispatch(logoutUser())],
        ...Object.entries(ExceptionCode)
          .filter(([key]) => !excludedKeys.includes(key))
          .map(([key, value]) => [value, (err) => handleShowErrorMessage(buildErrorMessage(t, key, err))]),
      ])
    );
  }, []);

  return null;
}
